<script setup lang="ts">
import { Button } from '@zerohuman/ui'
import { onMounted, reactive, ref } from 'vue'
import {
    type PersonProfile,
    PROFILE_LINK_LABELS,
    type ProfileLink,
    type ProfileLinkLabel,
    profilesApi,
} from '@/api-profiles'
import { type EnterpriseUserRow, UsersApiError } from '@/api-users'

/**
 * #1266: edit one person's public profile — job title, biography, social links and whether they are publicly
 * listed — beside the account name and avatar that already identify them. The name and avatar are the account's
 * and are not edited here; the save sends the four profile fields and nothing else, so no edit can carry a grant.
 */
const props = defineProps<{ person: EnterpriseUserRow }>()
const emit = defineEmits<{ saved: [profile: PersonProfile] }>()

const JOB_TITLE_MAX = 100
const BIOGRAPHY_MAX = 1000

const form = reactive({
    jobTitle: '',
    biography: '',
    links: Object.fromEntries(PROFILE_LINK_LABELS.map(label => [label, ''])) as Record<ProfileLinkLabel, string>,
    publiclyListed: false,
})
const loading = ref(true)
const saving = ref(false)
const error = ref<string | null>(null)

/** The id each link's input carries: `profile-link-linkedin`, `profile-link-x`, `profile-link-website`. */
function linkId(label: ProfileLinkLabel): string {
    return `profile-link-${label.toLowerCase()}`
}

function fill(profile: PersonProfile): void {
    form.jobTitle = profile.jobTitle ?? ''
    form.biography = profile.biography ?? ''
    for (const label of PROFILE_LINK_LABELS) {
        form.links[label] = profile.socialLinks.find(link => link.label === label)?.url ?? ''
    }
    form.publiclyListed = profile.publiclyListed === true
}

/** Blank is cleared: null for a blank text, and no link at all for a blank link. */
function blankAsNull(value: string): string | null {
    const trimmed = value.trim()
    return trimmed === '' ? null : trimmed
}

function why(failure: unknown, fallback: string): string {
    if (failure instanceof UsersApiError) {
        return failure.detail ?? failure.message
    }
    return fallback
}

onMounted(async () => {
    try {
        fill(await profilesApi.profile(props.person.id))
    } catch (failure) {
        error.value = why(failure, 'Could not load this profile.')
    } finally {
        loading.value = false
    }
})

async function save(): Promise<void> {
    saving.value = true
    error.value = null
    const socialLinks: ProfileLink[] = PROFILE_LINK_LABELS.flatMap(label => {
        const url = blankAsNull(form.links[label])
        return url ? [{ label, url }] : []
    })
    try {
        const saved = await profilesApi.updateProfile(props.person.id, {
            jobTitle: blankAsNull(form.jobTitle),
            biography: blankAsNull(form.biography),
            socialLinks,
            publiclyListed: form.publiclyListed,
        })
        emit('saved', saved)
    } catch (failure) {
        error.value = why(failure, 'Could not save this profile.')
    } finally {
        saving.value = false
    }
}
</script>

<template>
    <form class="space-y-3 text-sm" data-testid="person-profile-form" @submit.prevent="save">
        <div class="flex items-center gap-2">
            <img v-if="person.avatarUrl" :src="person.avatarUrl" alt="" class="size-8" />
            <span class="font-medium">{{ person.name }}</span>
        </div>
        <p v-if="loading" class="text-muted-foreground">Loading…</p>
        <label class="block space-y-1">
            <span>Job title <span class="text-muted-foreground text-xs">(up to {{ JOB_TITLE_MAX }} characters)</span></span>
            <input
                v-model="form.jobTitle"
                data-testid="profile-job-title"
                type="text"
                class="w-full border border-input bg-background px-2 py-1"
            />
        </label>
        <label class="block space-y-1">
            <span>Biography</span>
            <textarea
                v-model="form.biography"
                data-testid="profile-biography"
                rows="4"
                class="w-full border border-input bg-background px-2 py-1"
            />
            <span class="text-muted-foreground text-xs">Up to {{ BIOGRAPHY_MAX }} characters.</span>
        </label>
        <fieldset class="space-y-2">
            <legend>Links</legend>
            <label v-for="label in PROFILE_LINK_LABELS" :key="label" class="block space-y-1">
                <span>{{ label }}</span>
                <input
                    v-model="form.links[label]"
                    :data-testid="linkId(label)"
                    type="url"
                    placeholder="https://"
                    class="w-full border border-input bg-background px-2 py-1"
                />
            </label>
        </fieldset>
        <label class="flex items-center gap-2">
            <input v-model="form.publiclyListed" data-testid="profile-listed" type="checkbox" />
            <span>Show this person on the public website</span>
        </label>
        <p v-if="error" class="text-destructive" role="alert" data-testid="profile-error">{{ error }}</p>
        <Button type="button" size="sm" data-testid="profile-save" :disabled="saving || loading" @click="save">
            Save profile
        </Button>
    </form>
</template>
